import { Container } from 'react-bootstrap';
import { useContext, useEffect, useMemo } from 'react';

import { TabButton } from './tab-button';
import {
  PlayerChatContext,
  PlayerChatContextProps
} from '../../../../contexts/player-chat';
import { DrawerTabs } from './tabs/tabs';
import { Info as InfoTab } from './tabs/info';
import { Chat as ChatTab } from './tabs/chat';
import { Roles as RolesTab } from './tabs/roles';
import { Teams as TeamsTab } from './tabs/teams';
import { TradeScreen as TradeTab } from './tabs/trade';
import { GameContext } from '../../../../contexts/game';
import { PlayerContext } from '../../../../contexts/player';
import { Inventory as InventoryTab } from './tabs/inventory';
import { OverlayContext } from '../../../../contexts/overlay';
import { TradeStateContext } from '../../../../contexts/trade';
import { Scoreboard as ScoreboardTab } from './tabs/scoreboard';
import { OnDemandTask as OnDemandTaskTab } from './tabs/on-demand-task';
import { GameDocumentContext } from '../../../../contexts/game-document';
import { DisplayLanguageContext } from '../../../../contexts/display-languages';
import { generateTitleById } from '../../../../utils/game-document/display-languages';

export const Drawer = () => {
  const { newChat } = useContext(PlayerChatContext) as PlayerChatContextProps;
  const [overlay, setOverlay] = useContext(OverlayContext);
  const [player] = useContext(PlayerContext);
  const [state] = useContext(GameDocumentContext);
  const [game] = useContext(GameContext);
  const [displayLanguageContext] = useContext(DisplayLanguageContext);
  const [trade] = useContext(TradeStateContext);

  const playerCode = player.playerState?.code ?? '';
  const isInLobby = ['waiting', 'ready'].includes(
    player.playerState?.status ?? ''
  );
  const preGameSetting = state.gameDocument?.settings.preGame;
  const inGameSetting =
    game.gameState?.runtime?.inGame ?? state.gameDocument?.settings.inGame;

  // tab setting
  const showInfoTab = isInLobby ? true : inGameSetting?.showInGameInfo;
  const showTeamsTab = isInLobby
    ? preGameSetting?.showPreGameTeams
    : inGameSetting?.showInGameTeams;
  const showInventoryTab = isInLobby
    ? preGameSetting?.showPreGameInventory
    : inGameSetting?.showInGameInventory;
  const showRolesTab = isInLobby
    ? preGameSetting?.showPreGameRoles
    : inGameSetting?.showInGameRoles;
  const showChatTab = isInLobby
    ? preGameSetting?.showPreGameChat
    : inGameSetting?.showInGameChat;
  const showScoreboardTab = isInLobby
    ? preGameSetting?.showPreGameScoreboard
    : inGameSetting?.showInGameScoreboard;
  const showOnDemandTaskTab = isInLobby
    ? preGameSetting?.showPreGameOnDemandTask
    : inGameSetting?.showInGameOnDemandTask;

  // chat room setting
  const showAllPlayerChat = isInLobby
    ? preGameSetting?.showPreGameAllPlayerChat
    : inGameSetting?.showInGameAllPlayerChat;
  const showFacilitatorChat = isInLobby
    ? preGameSetting?.showPreGameFacilitatorChat
    : inGameSetting?.showInGameFacilitatorChat;
  const showTeamChat = isInLobby
    ? preGameSetting?.showPreGameTeamChat
    : inGameSetting?.showInGameTeamChat;
  const showDirectChat = isInLobby
    ? preGameSetting?.showPreGameDirectChat
    : inGameSetting?.showInGameDirectChat;

  const teamCode = player.playerState?.teamCode;
  const otherPlayers = (game.gameState?.players ?? []).filter(
    (p) => p.code !== player.playerState?.code
  );

  const isOnTradingSession = useMemo(() => {
    const offer = ['Draft', 'Pending'].includes(trade.offerPlayerStatus);
    const request = ['Draft', 'Pending'].includes(trade.requestPlayerStatus);
    return (
      [offer, request].every((e) => e) ||
      [trade.offerPlayerStatus, trade.requestPlayerStatus].some(
        (e) => e === 'Accepted'
      )
    );
  }, [trade.offerPlayerStatus, trade.requestPlayerStatus]);

  const isChatTabEnable = useMemo(() => {
    const team = showTeamChat && teamCode !== undefined;
    const direct = showDirectChat && otherPlayers.length > 0;

    const chatSetting = [team, direct, showAllPlayerChat, showFacilitatorChat];

    return showChatTab && chatSetting.some((c) => c === true);
  }, [
    teamCode,
    showChatTab,
    showTeamChat,
    showDirectChat,
    showAllPlayerChat,
    showFacilitatorChat,
    otherPlayers.length
  ]);

  const setActiveTab = (tab: DrawerTabs) => {
    setOverlay((prevState) => ({
      ...prevState,
      activeTab: tab
    }));
  };

  useEffect(() => {
    setOverlay((prev) => ({
      ...prev,
      activeTab:
        isOnTradingSession && !trade.showNotification ? 'trade' : 'info',
      drawerIsOpen: isOnTradingSession && !trade.showNotification
    }));
  }, [isOnTradingSession, trade.showNotification]);

  useEffect(() => {
    const tabs = [
      { enable: showInfoTab, tab: 'info' },
      { enable: showTeamsTab, tab: 'teams' },
      { enable: showInventoryTab, tab: 'inventory' },
      { enable: showRolesTab, tab: 'roles' },
      { enable: isChatTabEnable, tab: 'chat' },
      { enable: showScoreboardTab, tab: 'scoreboard' },
      { enable: showOnDemandTaskTab, tab: 'onDemandTask' }
    ];

    // move player active tab to the first shown tab setting
    // if the active tab is hidden by live setting facilitator
    const setToFirstActiveTab = () => {
      const firstActiveTab = tabs.find((t) => t.enable === true);
      if (firstActiveTab)
        setOverlay((prev) => ({
          ...prev,
          activeTab: firstActiveTab.tab
        }));
    };

    if (overlay.activeTab === 'info' && !showInfoTab) setToFirstActiveTab();
    if (overlay.activeTab === 'teams' && !showTeamsTab) setToFirstActiveTab();
    if (overlay.activeTab === 'inventory' && !showInventoryTab)
      setToFirstActiveTab();
    if (overlay.activeTab === 'roles' && !showRolesTab) setToFirstActiveTab();
    if (overlay.activeTab === 'chat' && !isChatTabEnable) setToFirstActiveTab();
    if (overlay.activeTab === 'scoreboard' && !showScoreboardTab)
      setToFirstActiveTab();
    if (overlay.activeTab === 'onDemandTask' && !showOnDemandTaskTab)
      setToFirstActiveTab();
  }, [
    overlay.activeTab,
    showInfoTab,
    showTeamsTab,
    showRolesTab,
    isChatTabEnable,
    showInventoryTab,
    showScoreboardTab,
    showOnDemandTaskTab
  ]);

  return (
    <>
      <Container className={'drawer__tabs px-0'} fluid>
        <div className={'d-none d-md-flex justify-content-between'}>
          <div className="d-flex flex-wrap gap-2 container-menu-button">
            {showInfoTab && (
              <TabButton
                thisTab={'info'}
                activeTab={overlay.activeTab}
                titleIcon={
                  generateTitleById(
                    '670d9f08-d761-4c71-883c-4265648d6a73',
                    state,
                    displayLanguageContext.displayLanguageSelected.resources!,
                    'game'
                  ) || 'Info'
                }
                icon="info"
                onSetActiveTab={setActiveTab}
                isDisabled={isOnTradingSession}
              />
            )}

            {showTeamsTab && (
              <TabButton
                thisTab={'teams'}
                activeTab={overlay.activeTab}
                titleIcon={
                  generateTitleById(
                    '4be208b6-39fc-434d-bae7-e39f6d1ed332',
                    state,
                    displayLanguageContext.displayLanguageSelected.resources!,
                    'game'
                  ) || 'Teams'
                }
                icon="group"
                onSetActiveTab={setActiveTab}
                isDisabled={isOnTradingSession}
              />
            )}

            {showInventoryTab && (
              <TabButton
                thisTab={'inventory'}
                activeTab={overlay.activeTab}
                titleIcon={
                  generateTitleById(
                    'f7a7c135-91e2-433f-a7ed-0b62ff39aba0',
                    state,
                    displayLanguageContext.displayLanguageSelected.resources!,
                    'game'
                  ) || 'Inventory'
                }
                icon="trip"
                onSetActiveTab={setActiveTab}
                isDisabled={isOnTradingSession}
              />
            )}

            {showRolesTab && (
              <TabButton
                thisTab={'roles'}
                activeTab={overlay.activeTab}
                titleIcon={
                  generateTitleById(
                    '832f9ead-d125-4418-aebb-ebdff00a3a8d',
                    state,
                    displayLanguageContext.displayLanguageSelected.resources!,
                    'game'
                  ) || 'Titles'
                }
                icon="settings_accessibility"
                onSetActiveTab={setActiveTab}
                isDisabled={isOnTradingSession}
              />
            )}

            {isChatTabEnable && (
              <TabButton
                thisTab={'chat'}
                activeTab={overlay.activeTab}
                titleIcon={
                  generateTitleById(
                    '57f9b876-0dcf-4e59-b240-2dc88aa9390e',
                    state,
                    displayLanguageContext.displayLanguageSelected.resources!,
                    'game'
                  ) || 'Chat'
                }
                icon="forum"
                onSetActiveTab={setActiveTab}
                isDisabled={isOnTradingSession}
                notificationCount={
                  newChat.filter((c) => c.author.id !== playerCode).length
                }
              />
            )}

            {showScoreboardTab && (
              <TabButton
                thisTab={'scoreboard'}
                activeTab={overlay.activeTab}
                titleIcon={
                  generateTitleById(
                    'e698200c-73e4-41e3-b4b1-fa4b38f4638c',
                    state,
                    displayLanguageContext.displayLanguageSelected.resources!,
                    'game'
                  ) || 'Scoreboard'
                }
                icon="leaderboard"
                onSetActiveTab={setActiveTab}
                isDisabled={isOnTradingSession}
              />
            )}

            {showOnDemandTaskTab && (
              <TabButton
                thisTab={'onDemandTask'}
                activeTab={overlay.activeTab}
                titleIcon={
                  generateTitleById(
                    '47750496-640c-46c5-945c-0a72fcc7a259',
                    state,
                    displayLanguageContext.displayLanguageSelected.resources!,
                    'game'
                  ) || 'On-demand tasks'
                }
                icon="task_alt"
                onSetActiveTab={setActiveTab}
                isDisabled={isOnTradingSession}
              />
            )}
          </div>
        </div>
      </Container>

      {overlay.activeTab === 'info' && <InfoTab />}
      {overlay.activeTab === 'teams' && <TeamsTab />}
      {overlay.activeTab === 'inventory' && <InventoryTab />}
      {overlay.activeTab === 'roles' && <RolesTab />}
      {overlay.activeTab === 'chat' && <ChatTab />}
      {overlay.activeTab === 'scoreboard' && <ScoreboardTab />}
      {overlay.activeTab === 'trade' && <TradeTab />}
      {overlay.activeTab === 'onDemandTask' && <OnDemandTaskTab />}
    </>
  );
};
